import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { createUnitsColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/UnitsColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { DATE_FORMATS } from '@dealstack/shared/lib/format/dates/date';
import { InvestorTransactionSummaryRes } from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface Props {
	tInvestor: TranslateFunction<I18Namespaces.INVESTOR>;
}

export const getTransactionsTableColumns = ({
	tInvestor,
}: Props): ExtendedColumnDef<InvestorTransactionSummaryRes>[] => [
	createDateColumn({
		id: 'date',
		headerText: tInvestor('transactions_lists.header.date'),
		accessorKey: 'investmentWorkflowUpdatedAt',
		dateFormat: DATE_FORMATS.CALENDAR_DATE,
		enableSorting: false,
	}),
	createTextColumn({
		id: 'security',
		headerText: tInvestor('transactions_lists.header.security'),
		accessorFn: (row) => {
			if (row.loan) {
				return `${row.loan.funder} (${row.loan.name})`;
			}
			if (row.securityName) {
				return `${row.securityIssuerDisplayName ?? ''} (${row.securityName})`;
			}
			return null;
		},
		enableSorting: false,
	}),
	createTextColumn({
		id: 'type',
		headerText: tInvestor('transactions_lists.header.type'),
		accessorFn: (row) => {
			if (typeof row.isNegative !== 'boolean') {
				return LONG_DASH;
			}
			return row.isNegative
				? tInvestor('transactions_lists.type.sell')
				: tInvestor('transactions_lists.type.buy');
		},
		enableSorting: false,
	}),
	createTextColumn({
		id: 'status',
		headerText: tInvestor('transactions_lists.header.status'),
		accessorFn: (row) =>
			row.isCompleted
				? tInvestor('transactions_lists.status.completed')
				: tInvestor('transactions_lists.status.pending'),
		enableSorting: false,
	}),
	createUnitsColumn({
		id: 'number',
		headerText: tInvestor('transactions_lists.header.number_of_units'),
		accessorKey: 'number',
		enableSorting: false,
	}),
	createNumberColumn({
		id: 'totalCost',
		headerText: tInvestor('transactions_lists.header.total_cost'),
		lockPrecision: true,
		accessorFn: (row) => ({
			value: row.isNegative === true ? -row.cost : row.cost,
			currency: row.currency,
		}),
		enableSorting: false,
	}),
];
